import { useCustomerStore } from '@/stores/customer'
import { CheckoutErrorType } from '@/stores/newCheckout'

interface AddressData {
  title?: string | null
  first_name: string
  last_name: string
  gender?: string | null
  company?: string | null
  street_name: string
  street_number: string
  additional?: string | null
  zip: string
  city: string
  country: string
}

/**
 * Ensures address fields are properly formatted for API payload
 * @param addressData - Raw address data from store
 * @returns Formatted address object with all required fields
 */
export function ensureAddressFields(addressData: AddressData): {
  title: string | null
  first_name: string
  last_name: string
  gender: string | null
  company: string | null
  street_name: string
  street_number: string
  additional: string | null
  zip: string
  city: string
  country: string
  is_default_shipping_address: boolean
  is_default_billing_address: boolean
} {
  return {
    title: addressData.title ?? null,
    first_name: addressData.first_name,
    last_name: addressData.last_name,
    gender: addressData.gender ?? null,
    company: addressData.company ?? null,
    street_name: addressData.street_name,
    street_number: addressData.street_number,
    additional: addressData.additional ?? null,
    zip: addressData.zip,
    city: addressData.city,
    country: addressData.country,
    is_default_shipping_address: false,
    is_default_billing_address: false
  }
}

/**
 * Extracts payment data from payment method object
 * Adds IP address if missing and extracts nonce
 * @param data - Payment method data
 * @returns Object with payment_method and braintree_nonce
 */
export function getPaymentData(data: any): {
  payment_method: any
  braintree_nonce?: string
} {
  const paymentMethod = { ...data }

  if (!paymentMethod.ip_address) {
    const customerStore = useCustomerStore()
    if (customerStore.profile?.ip_address) {
      paymentMethod.ip_address = customerStore.profile.ip_address
    }
  }

  return {
    payment_method: paymentMethod,
    braintree_nonce: data.tokenized_nonce ?? data.nonce
  }
}

/**
 * Builds base payload for order placement
 * @param cartStore - Cart store instance
 * @param email - Customer email
 * @param subscribeToNewsletter - Whether to subscribe to newsletter
 * @param checkoutType - Provide checkout type identifier for API
 * @returns Base payload object
 */
export function buildBasePayload(
  cartStore: any,
  email: string | null | undefined,
  subscribeToNewsletter: boolean | null | undefined,
  checkoutType?: 'guest' | 'express' | 'regular' | 'opc'
): { [key: string]: any } {
  const payload: { [key: string]: any } = {
    cart_uuid: cartStore.cartId,
    coupon_code: cartStore.storedCouponCode,
    use_credits: cartStore.creditsAreUsed,
    email: email,
    delivery_option_id: cartStore.vicampoDeliveryOptionId,
    miles_spent: cartStore.storedMiles
  }

  if (subscribeToNewsletter) {
    payload.newsletter_subscription = true
  }

  if (checkoutType) {
    payload.checkout_type = checkoutType
  }

  return payload
}

/**
 * Handles checkout errors by mapping error causes to CheckoutErrorType
 * and blacklisting payment methods when appropriate
 * @param error - The error object
 * @param useSameBillingAddress - Whether billing address is same as shipping
 * @param blacklistPaymentMethod - Function to blacklist a payment method
 * @param clearPaymentMethod - Function to clear payment method selection
 * @returns CheckoutErrorType that was set
 */
export function handleCheckoutError(
  error: any,
  useSameBillingAddress: boolean | null | undefined,
  blacklistPaymentMethod?: (idOrCode: number | string) => void,
  clearPaymentMethod?: () => void
): CheckoutErrorType {
  if (error.cause === 301) {
    const isShippingError = useSameBillingAddress === true
    const errorType = isShippingError
      ? CheckoutErrorType.SHIPPING_ADDRESS_ERROR
      : CheckoutErrorType.BILLING_ADDRESS_ERROR
    error.description = errorType
    return errorType
  }

  if (error.cause && error.cause >= 1040 && error.cause < 1050) {
    const errorType =
      error.cause === 1042
        ? CheckoutErrorType.SHIPPING_ADDRESS_ERROR
        : CheckoutErrorType.BILLING_ADDRESS_ERROR
    error.description = errorType
    return errorType
  }

  // Handle cart ownership errors (cart doesn't belong to user)
  if (error.cause === 1050) {
    const errorType = CheckoutErrorType.CART_OWNERSHIP_ERROR
    error.description = errorType
    return errorType
  }

  if (error.cause || error.status === 499 || error.status === 400) {
    const errorType = CheckoutErrorType.PAYMENT_METHOD_ERROR
    error.validationMessage = error.description || errorType
    error.description = errorType

    // Blacklist payment method if callback provided
    if (blacklistPaymentMethod && clearPaymentMethod) {
      blacklistPaymentMethod(error.paymentMethodId || error.paymentMethodCode)
      clearPaymentMethod()
    }

    return errorType
  }

  const errorType = CheckoutErrorType.FALLBACK_CREATE_ORDER_ERROR
  error.description = errorType
  return errorType
}
